import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Package, Loader2, Plus, Save, Trash2, RefreshCw, X, Pencil, RotateCcw, Layers, Database, Zap, Copy } from "lucide-react";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { formatEuro } from "@/lib/format";
import { AppHeader } from "@/components/AppHeader";
import { EuroInput } from "@/components/EuroInput";
import { useAuth } from "@/contexts/AuthContext";
import { RepriceUnsentButton, useRepriceAfterSave } from "@/components/offers/RepriceUnsentButton";

const inputCls = "h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium";
const labelCls = "block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold";
const eur = (v) => {
  try { return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(v) || 0); }
  catch { return `${v} €`; }
};

const CAT_LABELS = {
  hardware: "Wärmepumpen",
  speicher: "Speicher",
  hydraulisch: "Hydraulik",
  elektrisch: "Elektrik",
  montage: "Montage",
  beratung: "Beratung",
  addon: "Sonstiges",
};

const emptyDraft = () => ({
  brand: "HEIKO",
  kw: 9,
  min_kw: null,
  max_kw: null,
  name: "",
  description: "",
  abgabepreis_brutto: 0,
  pricing_mode: "bundle",
  items: [],
  leistung: {},
  active: true,
});

export default function AdminWpPackages({ embedded = false }) {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.is_super_admin;
  const [pkgs, setPkgs] = useState([]);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const repriceAfterSave = useRepriceAfterSave();
  const [editor, setEditor] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [pickerCategory, setPickerCategory] = useState("hardware");
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    setLoadError(null);
    try {
      const [{ data: pks }, { data: cmp }] = await Promise.all([
        api.get(`/admin/wp-packages?include_inactive=${showInactive}`),
        api.get("/admin/wp-components?include_inactive=false"),
      ]);
      setPkgs(pks || []);
      setComponents(cmp || []);
    } catch (e) {
      const msg = e.response?.data?.detail || "Daten konnten nicht geladen werden";
      setLoadError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [showInactive]);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const compMap = useMemo(() => {
    const m = {};
    for (const c of components) m[c.id] = c;
    return m;
  }, [components]);

  // ── Bracket-Analyse (Iteration 76) ────────────────────────────────────
  // Berechnet für das aktuell bearbeitete Paket:
  //   • `overlaps`      – Liste anderer Pakete derselben Marke, deren
  //                       manueller Bracket sich mit unserem überschneidet
  //                       (verhindert stille Fehl-Zuordnung)
  //   • `previewRange`  – der Bereich in dem DIESES Paket tatsächlich als
  //                       „recommended" gewählt würde (berücksichtigt, dass
  //                       ein engerer Bracket eines Konkurrenten in Teilen
  //                       gewinnt).
  // Rein clientseitige Rechnung — spart einen Backend-Roundtrip pro Tastendruck.
  const bracketAnalysis = useMemo(() => {
    if (!editor || editor.min_kw == null || editor.max_kw == null) {
      return { overlaps: [], previewRange: null, previewSegments: [] };
    }
    const lo = Number(editor.min_kw);
    const hi = Number(editor.max_kw);
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) {
      return { overlaps: [], previewRange: null, previewSegments: [], invalid: true };
    }

    // Kandidaten = alle aktiven Pakete derselben Marke, außer wir selbst.
    const brand = (editor.brand || "").trim().toUpperCase();
    const others = (pkgs || []).filter(
      (p) => (p.brand || "").toUpperCase() === brand
          && p.id !== editor.id
          && p.active !== false
          && p.min_kw != null && p.max_kw != null
    );

    // Overlaps: klassisch Intervall-Schnitt
    const overlaps = [];
    for (const o of others) {
      const oLo = Number(o.min_kw);
      const oHi = Number(o.max_kw);
      const ovLo = Math.max(lo, oLo);
      const ovHi = Math.min(hi, oHi);
      if (ovLo < ovHi) {
        // Bestimme wer engster wäre (engster gewinnt in der Recommender-Logik).
        const ourSpan = hi - lo;
        const theirSpan = oHi - oLo;
        overlaps.push({
          id: o.id,
          name: o.name,
          kw: o.kw,
          min_kw: oLo, max_kw: oHi,
          overlap_min: ovLo, overlap_max: ovHi,
          winner: ourSpan <= theirSpan ? "self" : "other",
        });
      }
    }

    // Preview-Segmente: wo in [lo,hi] gewinnen wir tatsächlich?
    // Ein Konkurrenz-Bracket „stiehlt" uns nur den Teilbereich [ovLo,ovHi]
    // wenn er ENGER ist. Bei gleichem Span gewinnt der zuerst Angelegte
    // (deterministisch aber Backend-Detail, für den UI-Hinweis egal).
    let coverage = [[lo, hi]];
    const stolen = [];
    for (const c of overlaps) {
      if (c.winner === "other") {
        // Diese Overlap-Region [c.overlap_min, c.overlap_max] wird nicht uns gehören
        const next = [];
        for (const [a, b] of coverage) {
          const oLo = Math.max(a, c.overlap_min);
          const oHi = Math.min(b, c.overlap_max);
          if (oLo >= oHi) { next.push([a, b]); continue; }
          if (a < oLo) next.push([a, oLo]);
          if (oHi < b) next.push([oHi, b]);
        }
        coverage = next;
        stolen.push({ name: c.name, from: c.overlap_min, to: c.overlap_max });
      }
    }
    return {
      overlaps,
      previewRange: [lo, hi],
      previewSegments: coverage,
      stolen,
    };
  }, [editor, pkgs]);

  const openNew = () => setEditor({ isNew: true, ...emptyDraft() });
  const openEdit = (pkg) => {
    const rawItems = (pkg.items || []).map(it => {
      // EIN Preis pro Item: MAX(ref, line) beseitigt die versteckte Zwilligs-Drift.
      // Nur wenn BEIDE Werte 0 sind, fällt der Editor auf den WP-Komponentenpreis zurück.
      // So kann der Admin bewusst abweichende Paket-Preise setzen (z. B. Rabatt-
      // Kalkulation) ohne dass alte Drift-Werte reingemischt werden.
      const compPrice = it.component_id ? Number((compMap[it.component_id] || {}).price_eur) || 0 : 0;
      const itemPrice = Math.max(Number(it.ref_price_net_eur) || 0, Number(it.line_price_net_eur) || 0);
      const unified = itemPrice > 0 ? itemPrice : compPrice;
      return {
        _key: it.component_id ? `cid:${it.component_id}` : `free:${Math.random().toString(36).slice(2, 10)}`,
        component_id: it.component_id || null,
        is_free: !!it.is_free || !it.component_id,
        title: it.title || "",
        qty: it.qty,
        note: it.note || "",
        ref_price_net_eur: unified,
        separate_line: !!it.separate_line,
        line_price_net_eur: unified,
      };
    });
    // Auto-Sync: der Editor öffnet IMMER mit der Live-Summe der Positionen,
    // nicht mit dem historisch gespeicherten `abgabepreis_brutto`. So kann
    // kein Drift bestehen bleiben. (Fix 09.07.2026: alte DB-Datensätze hatten
    // manuell überschriebene Preise, die nicht zur Item-Summe passten.)
    const liveAbgabe = _calcAbgabe(rawItems);
    setEditor({
      isNew: false, id: pkg.id,
      brand: pkg.brand, kw: pkg.kw,
      min_kw: pkg.min_kw ?? null,
      max_kw: pkg.max_kw ?? null,
      name: pkg.name || "", description: pkg.description || "",
      abgabepreis_brutto: liveAbgabe,
      pricing_mode: pkg.pricing_mode || "bundle",
      items: rawItems,
      leistung: pkg.leistung || {},
      active: pkg.active !== false,
    });
  };

  // Helper: berechnet Abgabepreis brutto aus items (Σ Netto × qty × 1,19)
  // Wird bei JEDER Item-Änderung aufgerufen — der Abgabepreis ist damit
  // ausschließlich das Live-Ergebnis der Einzelpositionen (kein manueller Drift).
  const _calcAbgabe = (items) => {
    const net = items.reduce((s, it) => {
      const price = Number(it.line_price_net_eur) || Number(it.ref_price_net_eur) || 0;
      return s + price * (Number(it.qty) || 0);
    }, 0);
    return Math.round(net * 1.19 * 100) / 100;
  };

  const addItem = (componentId) => {
    if (!editor) return;
    if (editor.items.some(i => i.component_id === componentId)) {
      toast.info("Komponente schon im Paket – stattdessen Menge erhöhen");
      return;
    }
    const c = compMap[componentId] || {};
    const price = Math.max(0, Number(c.price_eur) || 0);
    const newItems = [...editor.items, {
      _key: `cid:${componentId}`,
      component_id: componentId,
      is_free: false,
      title: "",
      qty: 1, note: "",
      ref_price_net_eur: price,
      separate_line: false,
      line_price_net_eur: price,
    }];
    setEditor({ ...editor, items: newItems, abgabepreis_brutto: _calcAbgabe(newItems) });
  };

  const addFreeLine = () => {
    if (!editor) return;
    const key = `free:${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
    const newItems = [...editor.items, {
      _key: key,
      component_id: null,
      is_free: true,
      title: "",
      qty: 1, note: "",
      ref_price_net_eur: 0,
      separate_line: false,
      line_price_net_eur: 0,
    }];
    setEditor({ ...editor, items: newItems, abgabepreis_brutto: _calcAbgabe(newItems) });
  };

  const updateItem = (key, patch) => {
    const newItems = editor.items.map(i => i._key === key ? { ...i, ...patch } : i);
    setEditor({ ...editor, items: newItems, abgabepreis_brutto: _calcAbgabe(newItems) });
  };
  const updateQty = (key, qty) => updateItem(key, { qty: Math.max(0, Number(qty) || 0) });
  // Ein Preis pro Item — ref und line werden IMMER synchron gehalten.
  const updateRefPrice = (key, val) => {
    const p = Math.max(0, Number(val) || 0);
    updateItem(key, { ref_price_net_eur: p, line_price_net_eur: p });
  };
  const updateSeparateLine = (key, val) => updateItem(key, { separate_line: !!val });
  const updateLinePrice = (key, val) => {
    const p = Math.max(0, Number(val) || 0);
    updateItem(key, { line_price_net_eur: p, ref_price_net_eur: p });
  };
  const updateNote = (key, note) => updateItem(key, { note });
  const updateFreeTitle = (key, title) => updateItem(key, { title });
  const removeItem = (key) => {
    const newItems = editor.items.filter(i => i._key !== key);
    setEditor({ ...editor, items: newItems, abgabepreis_brutto: _calcAbgabe(newItems) });
  };

  // One-Click: alle katalog-basierten Items bekommen ihre WP-Komponenten-Preise
  // (ref + line) — ideal um den Abgabepreis = Summe umzusetzen ohne händisches Tippen.
  const pullPricesFromComponents = () => {
    if (!editor) return;
    let updated = 0;
    const next = editor.items.map(it => {
      if (it.is_free || !it.component_id) return it;
      const c = compMap[it.component_id];
      if (!c) return it;
      const p = Math.max(0, Number(c.price_eur) || 0);
      if (p <= 0) return it;
      if (it.ref_price_net_eur !== p || it.line_price_net_eur !== p) {
        updated += 1;
        return { ...it, ref_price_net_eur: p, line_price_net_eur: p };
      }
      return it;
    });
    setEditor({ ...editor, items: next, abgabepreis_brutto: _calcAbgabe(next) });
    if (updated === 0) toast.info("Preise sind bereits synchron mit WP-Komponenten.");
    else toast.success(`${updated} Position(en) aus WP-Komponenten aktualisiert · Abgabepreis nachgezogen.`);
  };

  // Live-Summe (nur Anzeige) — wird aus editor.items reaktiv gerechnet
  const linesSumNet = useMemo(() => {
    if (!editor) return 0;
    return (editor.items || []).reduce((s, it) => {
      const price = Number(it.line_price_net_eur) || Number(it.ref_price_net_eur) || 0;
      return s + price * (Number(it.qty) || 0);
    }, 0);
  }, [editor]);
  const linesSumGross = useMemo(() => Math.round(linesSumNet * 1.19 * 100) / 100, [linesSumNet]);

  const applySumAsAbgabepreis = () => {
    if (!editor) return;
    setEditor({ ...editor, abgabepreis_brutto: linesSumGross });
    toast.success(`Abgabepreis auf ${formatEuro(linesSumGross)} gesetzt.`);
  };

  const filteredComponents = useMemo(
    () => components.filter(c => c.category === pickerCategory && c.active !== false),
    [components, pickerCategory],
  );

  const itemsHydrated = useMemo(() => {
    if (!editor) return [];
    return editor.items.map(it => ({
      ...it,
      comp: it.component_id ? compMap[it.component_id] : null,
    }));
  }, [editor, compMap]);

  const save = async () => {
    if (!editor) return;
    if (!editor.name.trim()) { toast.error("Bitte Paket-Namen vergeben"); return; }
    if (!editor.brand.trim()) { toast.error("Bitte Marke angeben"); return; }
    setSaving(true);
    try {
      const payload = {
        brand: editor.brand.trim(),
        kw: Number(editor.kw) || 0,
        min_kw: (editor.min_kw === "" || editor.min_kw == null) ? null : Number(editor.min_kw),
        max_kw: (editor.max_kw === "" || editor.max_kw == null) ? null : Number(editor.max_kw),
        name: editor.name.trim(),
        description: editor.description || "",
        abgabepreis_brutto: Number(editor.abgabepreis_brutto) || 0,
        pricing_mode: editor.pricing_mode || "bundle",
        items: editor.items.map(i => ({
          component_id: i.is_free ? null : (i.component_id || null),
          is_free: !!i.is_free,
          title: i.is_free ? (i.title || "").trim() : null,
          qty: Number(i.qty) || 1,
          note: i.note || "",
          ref_price_net_eur: Number(i.ref_price_net_eur) || 0,
          separate_line: !!i.separate_line,
          line_price_net_eur: Number(i.line_price_net_eur) || 0,
        })),
        leistung: editor.leistung || {},
        active: editor.active,
      };
      if (editor.isNew) {
        await api.post("/admin/wp-packages", payload);
        toast.success("Paket angelegt");
      } else {
        await api.patch(`/admin/wp-packages/${editor.id}`, payload);
        toast.success("Paket aktualisiert");
      }
      const savedId = editor.isNew ? null : editor.id;
      setEditor(null);
      await load();
      if (savedId) repriceAfterSave.check({ package_id: savedId }, payload.name);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const remove = async (pkg) => {
    if (!window.confirm(`Paket "${pkg.name}" deaktivieren? Bestehende Konfigurationen bleiben unverändert.`)) return;
    try {
      await api.delete(`/admin/wp-packages/${pkg.id}`);
      toast.success("Deaktiviert");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen"); }
  };

  const reactivate = async (pkg) => {
    try {
      await api.post(`/admin/wp-packages/${pkg.id}/reactivate`);
      toast.success("Reaktiviert");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen"); }
  };

  const duplicatePackage = async (pkg) => {
    // Iteration 78: interaktiver Namens-Prompt — spart den Klick im Editor
    const suggested = `${pkg.name || "Paket"} (Kopie)`;
    const chosen = window.prompt("Name der Kopie:", suggested);
    if (chosen === null) return; // Abbrechen
    const finalName = chosen.trim() || suggested;
    try {
      const { data: copy } = await api.post(`/admin/wp-packages/${pkg.id}/duplicate`);
      // Falls der User einen anderen Namen als den Default gewählt hat → PATCHen.
      // (Zwei Requests, aber der Duplicate-Endpoint braucht keinen Body → einfacher.)
      if (finalName !== copy.name) {
        try {
          await api.patch(`/admin/wp-packages/${copy.id}`, {
            ...copy,
            name: finalName,
            // PATCH-Endpoint erwartet WpPackage-Shape — items im Original-Format zurück
            items: copy.items || [],
          });
          copy.name = finalName;
        } catch { /* Fallback: Kopie bleibt mit dem Default-Namen bestehen */ }
      }
      toast.success(`Kopiert: „${finalName}"`);
      await load();
      openEdit({ ...copy, name: finalName });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kopieren fehlgeschlagen");
    }
  };

  const runMigration = async ({ dryRun = false, allTenants = false } = {}) => {
    const label = dryRun ? "Dry-Run" : (allTenants ? "Migration (ALLE WP-Mandanten)" : "Migration");
    if (!dryRun) {
      const scope = allTenants ? "ALLE Mandanten mit WP-Modul" : "diesen Mandanten";
      if (!window.confirm(
        `${label} jetzt ausführen?\n\nEs werden die HEIKO- und LG-Legacy-Pakete (5+5) sowie ~30 Komponenten für ${scope} in die Datenbank geschrieben.\n\nDer Vorgang ist idempotent — vorhandene Legacy-Seed-Einträge werden zuerst entfernt und dann neu geschrieben.`,
      )) return;
    }
    setMigrating(true);
    setMigrateResult(null);
    try {
      const body = { dry_run: dryRun };
      if (allTenants) body.all_wp_tenants = true;
      const { data } = await api.post("/admin/wp-catalog/trigger-migration", body);
      setMigrateResult(data);
      const t = data.totals || {};
      toast.success(
        `${label} erfolgreich: ${t.components || 0} Komponenten · ${t.packages || 0} Pakete` +
        (t.cleaned ? ` (${t.cleaned} vorher bereinigt)` : ""),
      );
      if (!dryRun) await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Migration fehlgeschlagen");
    } finally {
      setMigrating(false);
    }
  };

  const fixNovatusAddress = async ({ dryRun = false } = {}) => {
    if (!dryRun && !window.confirm(
      "Firmenadresse jetzt korrigieren?\n\nSetzt die Anschrift des NOVATUS-Mandanten auf:\n\nMaximiliankorso 60\n13465 Berlin\n\nSowie Kontakt, IBAN, HRB, USt-ID, Geschäftsführer.\n\nIdempotent — mehrfach klicken ist sicher. Manuell gesetzte Adressen werden NICHT überschrieben.",
    )) return;
    setMigrating(true);
    try {
      const { data } = await api.post("/admin/tenant/fix-novatus-address", { dry_run: dryRun });
      const tRes = (data.tenants || []);
      const updated = tRes.filter(x => x.action === "updated").length;
      const skipped = tRes.filter(x => x.action === "skipped_manual_override").length;
      toast.success(
        dryRun
          ? `Dry-Run: ${tRes.length} Mandant(en) geprüft — ${updated + tRes.filter(x=>x.action==='would_update').length} würden aktualisiert`
          : `Firmenadresse aktualisiert: ${updated} geändert · ${skipped} übersprungen (manuelle Anschrift)`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Adress-Fix fehlgeschlagen");
    } finally {
      setMigrating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-wp-packages">
      {!embedded && <AppHeader title="WP-Pakete" back="/dashboard" />}
      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-3">
            <Package className="text-[#00E575]" size={22} />
            <div>
              <h1 className="font-display text-xl sm:text-2xl font-bold">WP-Pakete</h1>
              <p className="text-[12px] text-zinc-300 font-medium">Wärmepumpen-Komplettsets mit Festpreis · pro Marke/kW-Klasse</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[12px] text-zinc-400 inline-flex items-center gap-2 cursor-pointer font-medium">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}
                data-testid="wp-pkgs-toggle-inactive" className="accent-[#00E575]" />
              Inaktive
            </label>
            <button onClick={load} data-testid="wp-pkgs-reload"
              className="h-9 px-3 rounded-lg bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/40 text-[12px] inline-flex items-center gap-1.5 font-medium">
              <RefreshCw size={13} /> Aktualisieren
            </button>
            <RepriceUnsentButton />
            {repriceAfterSave.dialog}
            <button onClick={openNew} data-testid="wp-pkgs-new"
              className="h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964]">
              <Plus size={14} /> Neues Paket
            </button>
          </div>
        </div>

        {/* Legacy-Migration (nur Admin/Super-Admin) */}
        <div className="bg-[#0F0F11] border border-zinc-800 rounded-2xl p-3 sm:p-4"
             data-testid="wp-pkgs-migration-panel">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Database size={15} className="text-amber-400" />
                <span className="text-[12px] font-bold uppercase tracking-widest text-zinc-100">
                  Legacy-Daten migrieren
                </span>
              </div>
              <p className="text-[11.5px] text-zinc-400 max-w-2xl leading-relaxed">
                Schreibt die alten HEIKO/LG-Standardpakete (5+5) und ~30 Komponenten aus dem Code-Katalog
                in die Datenbank. <b className="text-zinc-200">Idempotent</b> — kann mehrfach ausgeführt werden.
                Nutze dies nach dem ersten Deploy auf ein Live-System, damit die Live-Datenbank die
                Katalog-Basis enthält.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 min-w-0">
              <button onClick={() => runMigration({ dryRun: true })} disabled={migrating}
                data-testid="wp-pkgs-migrate-dryrun"
                className="h-9 px-3 rounded-lg bg-[#18181B] border border-zinc-700 hover:border-amber-500/40 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50 font-medium">
                {migrating ? <Loader2 size={13} className="animate-spin" /> : <Database size={13} />} Dry-Run
              </button>
              <button onClick={() => runMigration({ dryRun: false, allTenants: false })} disabled={migrating}
                data-testid="wp-pkgs-migrate-run"
                className="h-9 px-3 rounded-lg bg-amber-500/15 border border-amber-500/50 text-amber-200 hover:bg-amber-500/25 text-[12px] inline-flex items-center gap-1.5 font-bold disabled:opacity-50">
                {migrating ? <Loader2 size={13} className="animate-spin" /> : <Database size={13} />}
                Für aktuellen Mandanten
              </button>
              {isSuperAdmin && (
                <button onClick={() => runMigration({ dryRun: false, allTenants: true })} disabled={migrating}
                  data-testid="wp-pkgs-migrate-all"
                  className="h-9 px-3 rounded-lg bg-red-500/15 border border-red-500/50 text-red-200 hover:bg-red-500/25 text-[12px] inline-flex items-center gap-1.5 font-bold disabled:opacity-50">
                  {migrating ? <Loader2 size={13} className="animate-spin" /> : <Database size={13} />}
                  Alle WP-Mandanten (Super-Admin)
                </button>
              )}
              <button onClick={() => fixNovatusAddress({ dryRun: false })} disabled={migrating}
                data-testid="wp-pkgs-fix-address"
                title="Setzt die Firmenanschrift auf Maximiliankorso 60, 13465 Berlin (idempotent)"
                className="h-9 px-3 rounded-lg bg-blue-500/15 border border-blue-500/50 text-blue-200 hover:bg-blue-500/25 text-[12px] inline-flex items-center gap-1.5 font-bold disabled:opacity-50">
                {migrating ? <Loader2 size={13} className="animate-spin" /> : <Database size={13} />}
                Firmenadresse fixen
              </button>
            </div>
          </div>
          {migrateResult && (
            <div className="mt-3 pt-3 border-t border-zinc-800 text-[11.5px] text-zinc-300"
                 data-testid="wp-pkgs-migrate-result">
              <div className="mb-1">
                <b>{migrateResult.dry_run ? "Dry-Run" : "Migration"}</b> —{" "}
                {(migrateResult.tenants || []).length} Mandant(en) verarbeitet.
                <span className="text-zinc-500 ml-2">Quelle: <code className="text-zinc-400">{migrateResult.source_tag}</code></span>
              </div>
              <ul className="space-y-0.5">
                {(migrateResult.tenants || []).map((t) => (
                  <li key={t.tenant_id} className="text-[12px]">
                    · <b className="text-zinc-100">{t.tenant_name || t.tenant_id}</b>{" "}
                    → {t.components} Komponenten · {t.packages} Pakete
                    {t.cleaned ? <span className="text-zinc-500"> (bereinigt: {t.cleaned})</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {loading && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 flex items-center justify-center gap-2 text-zinc-200 font-semibold">
            <Loader2 size={16} className="animate-spin" /> lädt…
          </div>
        )}

        {!loading && loadError && (
          <div className="bg-amber-500/10 border border-amber-500/40 rounded-2xl p-4 text-amber-200 text-sm">
            <b>Konnte Pakete nicht laden:</b> {loadError}
            {loadError.toLowerCase().includes("tenant") && (
              <div className="mt-2 text-[12px] text-amber-100 font-medium">
                Tipp: Als Super-Admin bitte oben rechts einen Mandanten im Selector wählen.
              </div>
            )}
          </div>
        )}

        {!loading && !loadError && pkgs.length === 0 && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-300 text-sm font-medium">
            Noch keine WP-Pakete für diesen Mandanten. Klick auf <b className="text-[#00E575]">Neues Paket</b>.
          </div>
        )}

        {!loading && pkgs.length > 0 && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-hidden">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#0F0F11] text-zinc-400 border-b border-zinc-800">
                <tr>
                  <th className="text-left px-3 py-2 font-bold uppercase tracking-widest text-[12px]">Marke</th>
                  <th className="text-left px-3 py-2 font-bold uppercase tracking-widest text-[12px]">Paket</th>
                  <th className="text-right px-3 py-2 font-bold uppercase tracking-widest text-[12px]">kW</th>
                  <th className="text-center px-3 py-2 font-bold uppercase tracking-widest text-[12px]">Heizlast-Bracket</th>
                  <th className="text-right px-3 py-2 font-bold uppercase tracking-widest text-[12px]">Bestandteile</th>
                  <th className="text-right px-3 py-2 font-bold uppercase tracking-widest text-[12px]">Brutto</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {pkgs.map((p) => {
                  // Bruttopreis wird IMMER live aus den Einzelpositionen berechnet
                  // (Σ line_price_net × qty × 1.19). Preis-Fallback-Chain:
                  // line_price_net_eur → ref_price_net_eur → wp_components.price_eur.
                  // Das früher separat gespeicherte `abgabepreis_brutto`-Feld
                  // wurde entfernt, weil es unbemerkt von der Item-Summe
                  // abdriften konnte (Bug 09.07.2026).
                  const itemsNet = (p.items || []).reduce((s, it) => {
                    const compPrice = it.component_id ? Number((compMap[it.component_id] || {}).price_eur) || 0 : 0;
                    const price = Number(it.line_price_net_eur) || Number(it.ref_price_net_eur) || compPrice;
                    return s + price * (Number(it.qty) || 0);
                  }, 0);
                  const itemsGross = Math.round(itemsNet * 1.19 * 100) / 100;
                  const storedGross = Number(p.abgabepreis_brutto) || 0;
                  const drift = Math.round((storedGross - itemsGross) * 100) / 100;
                  const hasDrift = Math.abs(drift) >= 0.5;
                  return (
                  <tr key={p.id} data-testid={`wp-pkg-row-${p.id}`}
                      className={`border-b border-zinc-900 hover:bg-[#0F0F11] ${!p.active ? "opacity-60" : ""} ${p.is_inherited ? "bg-sky-500/[0.03]" : ""}`}>
                    <td className="px-3 py-2 font-bold">
                      {p.brand}
                      {p.is_inherited && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-500/15 border border-sky-500/40 text-sky-300 text-[9px] font-bold uppercase tracking-wider"
                          data-testid={`wp-pkg-inherited-${p.id}`}
                          title='Geerbt vom Eltern-Mandanten. Zum Individualisieren: "Als eigene Kopie duplizieren".'
                        >
                          Geerbt
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-zinc-100 font-semibold">{p.name}</div>
                      {p.description && <div className="text-[10.5px] text-zinc-500 truncate max-w-md">{p.description}</div>}
                    </td>
                    <td className="px-3 py-2 text-right font-bold">{p.kw}</td>
                    <td className="px-3 py-2 text-center text-[12px] text-zinc-400">
                      {p.target_heat_load_min != null ? `${p.target_heat_load_min}–${p.target_heat_load_max}` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right text-[12px] text-zinc-400">{(p.items || []).length}</td>
                    <td className="px-3 py-2 text-right font-bold text-[#00E575]"
                        data-testid={`wp-pkg-gross-${p.id}`}>
                      {eur(itemsGross)}
                      {hasDrift && (
                        <div
                          className="text-[9px] font-bold uppercase tracking-widest text-amber-400 mt-0.5"
                          title={`Historisch gespeichert: ${eur(storedGross)} — driftet um ${eur(drift)} von der Item-Summe. Beim Öffnen & Speichern wird auto-synchronisiert.`}
                        >
                          Drift {drift > 0 ? "+" : ""}{eur(drift)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {p.is_inherited ? (
                        <button onClick={() => duplicatePackage(p)} data-testid={`wp-pkg-duplicate-${p.id}`}
                          title="Als eigenes Paket dieses Mandanten duplizieren und individualisieren"
                          className="h-7 px-2 rounded bg-sky-500/15 border border-sky-500/50 text-sky-200 hover:bg-sky-500/25 inline-flex items-center gap-1 text-[11px] font-semibold">
                          <Copy size={11} /> Als eigene Kopie
                        </button>
                      ) : (
                        <>
                          <button onClick={() => openEdit(p)} data-testid={`wp-pkg-edit-${p.id}`}
                            title="Bearbeiten"
                            className="h-7 w-7 rounded bg-zinc-800 hover:bg-zinc-700 inline-flex items-center justify-center mr-1">
                            <Pencil size={12} />
                          </button>
                          <button onClick={() => duplicatePackage(p)} data-testid={`wp-pkg-duplicate-${p.id}`}
                            title="Kopieren (Duplikat als neues Paket)"
                            className="h-7 w-7 rounded bg-sky-500/10 border border-sky-500/40 text-sky-300 hover:bg-sky-500/20 inline-flex items-center justify-center mr-1">
                            <Copy size={12} />
                          </button>
                          {p.active ? (
                            <button onClick={() => remove(p)} data-testid={`wp-pkg-delete-${p.id}`}
                              title="Deaktivieren"
                              className="h-7 w-7 rounded bg-red-500/10 border border-red-500/40 text-red-400 hover:bg-red-500/20 inline-flex items-center justify-center">
                              <Trash2 size={12} />
                            </button>
                          ) : (
                            <button onClick={() => reactivate(p)} data-testid={`wp-pkg-reactivate-${p.id}`}
                              title="Reaktivieren"
                              className="h-7 w-7 rounded bg-amber-500/10 border border-amber-500/40 text-amber-400 hover:bg-amber-500/20 inline-flex items-center justify-center">
                              <RotateCcw size={12} />
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {editor && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto"
             data-testid="wp-pkg-editor">
          <div className="bg-[#18181B] border border-zinc-700 rounded-2xl max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-4">
              <div className="flex items-center gap-2">
                <Layers size={18} className="text-[#00E575]" />
                <h2 className="font-display text-lg font-bold">
                  {editor.isNew ? "Neues WP-Paket" : `Paket bearbeiten: ${editor.name || "—"}`}
                </h2>
              </div>
              <button onClick={() => setEditor(null)} data-testid="wp-pkg-editor-close"
                className="h-8 w-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 inline-flex items-center justify-center">
                <X size={14} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Grunddaten */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Marke</label>
                  <input value={editor.brand} onChange={(e) => setEditor({ ...editor, brand: e.target.value })}
                    data-testid="wp-pkg-editor-brand" className={inputCls} placeholder="HEIKO / LG / …" />
                </div>
                <div>
                  <label className={labelCls}>kW-Klasse</label>
                  <input type="number" min="1" max="200" value={editor.kw}
                    onChange={(e) => setEditor({ ...editor, kw: Number(e.target.value) || 0 })}
                    data-testid="wp-pkg-editor-kw" className={inputCls} />
                </div>
              </div>

              {/* Heizlast-Brackets (Iteration 75) — Kaskaden-fähige Min/Max-Angabe */}
              <div className="rounded-xl border border-zinc-800 bg-[#0a0a0c] p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-[13px] font-bold text-zinc-100">Heizlast-Bereich (Bracket)</div>
                    <div className="text-[11px] text-zinc-400 leading-tight">
                      Legt fest, ab welcher Heizlast dieses Paket im Konfigurator vorgeschlagen wird. Leer lassen → System berechnet automatisch aus den kW-Klassen. <b>Für Kaskaden-Pakete (2+ Wärmepumpen, 20–100 kW) beide Werte ausfüllen.</b>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Min. Heizlast (kW)</label>
                    <input
                      type="number" step="0.1" min="0" max="500"
                      value={editor.min_kw ?? ""}
                      onChange={(e) => setEditor({ ...editor, min_kw: e.target.value === "" ? null : Number(e.target.value) })}
                      data-testid="wp-pkg-editor-min-kw"
                      className={inputCls}
                      placeholder="z.B. 20"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Max. Heizlast (kW)</label>
                    <input
                      type="number" step="0.1" min="0" max="500"
                      value={editor.max_kw ?? ""}
                      onChange={(e) => setEditor({ ...editor, max_kw: e.target.value === "" ? null : Number(e.target.value) })}
                      data-testid="wp-pkg-editor-max-kw"
                      className={inputCls}
                      placeholder="z.B. 40"
                    />
                  </div>
                </div>
                {(editor.min_kw != null || editor.max_kw != null) && (() => {
                  const { overlaps, previewSegments, stolen, invalid } = bracketAnalysis;
                  if (invalid) {
                    return (
                      <div data-testid="wp-pkg-bracket-invalid" className="mt-2 text-[11px] font-medium rounded-md px-2.5 py-1.5 bg-red-500/10 border border-red-500/40 text-red-300">
                        ⚠ Ungültiger Bereich: Max muss größer als Min sein.
                      </div>
                    );
                  }
                  const hasBothSet = editor.min_kw != null && editor.max_kw != null;
                  const conflicts = overlaps.filter((o) => o.winner === "self" || o.winner === "other");
                  return (
                    <div className="mt-2 space-y-1.5">
                      {/* Empfehlungs-Preview */}
                      {hasBothSet && previewSegments && (
                        <div
                          data-testid="wp-pkg-bracket-preview"
                          className={`text-[11px] font-medium rounded-md px-2.5 py-2 border ${
                            previewSegments.length === 0
                              ? "bg-amber-500/10 border-amber-500/40 text-amber-300"
                              : "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                          }`}
                        >
                          {previewSegments.length === 0 ? (
                            <>
                              <b>Achtung:</b> Dieses Paket würde <b>NIE</b> empfohlen werden — engere Konkurrenz-Brackets decken den kompletten Bereich {editor.min_kw} – {editor.max_kw} kW ab.
                            </>
                          ) : (
                            <>
                              <b>Wird empfohlen bei Heizlast:</b>{" "}
                              {previewSegments.map(([a, b], i) => (
                                <span key={i} className="font-bold">{a} – {b} kW{i < previewSegments.length - 1 ? ", " : ""}</span>
                              ))}
                              {stolen.length > 0 && (
                                <div className="text-[10.5px] mt-1 text-emerald-200/70">
                                  Aus {editor.min_kw} – {editor.max_kw} kW verlierst du {stolen.map((s) => `${s.from}–${s.to} kW an "${s.name}"`).join(", ")} — dort gewinnt der engere Bracket.
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      {/* Doppelt-Coverage-Warnung */}
                      {hasBothSet && conflicts.length > 0 && (
                        <div
                          data-testid="wp-pkg-bracket-overlap-warning"
                          className="text-[11px] font-medium rounded-md px-2.5 py-2 border bg-amber-500/10 border-amber-500/40 text-amber-200"
                        >
                          <div className="font-bold text-amber-300 mb-1">
                            ⚠ Bracket überschneidet sich mit {conflicts.length} {conflicts.length === 1 ? "anderen Paket" : "anderen Paketen"}
                          </div>
                          <ul className="space-y-0.5">
                            {conflicts.map((c) => (
                              <li key={c.id} className="flex items-center justify-between gap-2">
                                <span>
                                  „<b>{c.name}</b>&ldquo; ({c.min_kw}–{c.max_kw} kW) · Überschneidung bei <b>{c.overlap_min}–{c.overlap_max} kW</b>
                                </span>
                                <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                                  c.winner === "self"
                                    ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                                    : "bg-red-500/20 text-red-300 border border-red-500/40"
                                }`}>
                                  {c.winner === "self" ? "Wir gewinnen (enger)" : "Konkurrent gewinnt"}
                                </span>
                              </li>
                            ))}
                          </ul>
                          <div className="text-[10.5px] mt-1.5 text-amber-200/70">
                            Regel: Bei zwei sich überschneidenden manuellen Brackets gewinnt der engere. Bei gleichem Span gewinnt das zuerst angelegte Paket. Setze klare Grenzen, um stille Fehl-Zuordnungen zu vermeiden.
                          </div>
                        </div>
                      )}
                      {/* Fallback wenn nur EIN Wert gesetzt ist */}
                      {!hasBothSet && (
                        <div className="text-[11px] font-medium rounded-md px-2.5 py-1.5 bg-zinc-800/60 border border-zinc-700 text-zinc-400">
                          Bitte <b>Min UND Max</b> ausfüllen — sonst greift wieder die Auto-Berechnung.
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className={labelCls}>
                    Abgabepreis brutto (€)
                    <span className="ml-1 text-[9px] font-normal normal-case tracking-normal text-sky-300">
                      · live = Σ Bestandteile × 1,19
                    </span>
                  </label>
                  <EuroInput value={editor.abgabepreis_brutto}
                    onChange={(v) => setEditor({ ...editor, abgabepreis_brutto: v })}
                    testid="wp-pkg-editor-price"
                    className={inputCls}
                    disabled={true} />
                  {(() => {
                    const drift = Math.abs((editor.abgabepreis_brutto || 0) - linesSumGross);
                    if (drift < 0.01) return null;
                    return (
                      <button type="button" onClick={applySumAsAbgabepreis}
                        data-testid="wp-pkg-editor-apply-sum"
                        title="Der gespeicherte Preis weicht von der Live-Summe der Bestandteile ab — Klick übernimmt die Live-Summe."
                        className="mt-1 h-7 w-full text-[10.5px] font-bold uppercase tracking-widest rounded bg-amber-500/10 border border-amber-500/40 text-amber-300 hover:bg-amber-500/20 inline-flex items-center justify-center gap-1">
                        ⚠ DB-Wert weicht ab → Live-Summe übernehmen ({formatEuro(linesSumGross)})
                      </button>
                    );
                  })()}
                </div>
              </div>
              {/* Preis-Modus für Angebots-Konfigurator */}
              <div className="rounded-xl border border-zinc-800 bg-[#0a0a0c] p-3">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="text-[10.5px] uppercase tracking-widest font-bold text-zinc-300">Preis-Modus im Angebot</div>
                    <div className="text-[12px] text-zinc-300 leading-snug mt-0.5">
                      <b className="text-zinc-300">Pauschal-Bundle</b>: 1 Zeile zum Festpreis (klassisch). ·
                      {" "}<b className="text-sky-300">Einzelpositionen</b>: jedes Item als eigene Zeile mit eigenem Netto-Preis (unten pflegen).
                    </div>
                  </div>
                  <div className="flex gap-1 rounded-lg bg-[#09090B] border border-zinc-800 p-1 shrink-0">
                    <button type="button" onClick={() => setEditor({ ...editor, pricing_mode: "bundle" })}
                      data-testid="wp-pkg-editor-mode-bundle"
                      className={`h-8 px-3 text-[12px] font-bold uppercase tracking-widest rounded ${editor.pricing_mode !== "line_items" ? "bg-[#00E575] text-[#09090B]" : "text-zinc-400 hover:text-zinc-200"}`}>
                      Pauschal
                    </button>
                    <button type="button" onClick={() => setEditor({ ...editor, pricing_mode: "line_items" })}
                      data-testid="wp-pkg-editor-mode-lineitems"
                      className={`h-8 px-3 text-[12px] font-bold uppercase tracking-widest rounded ${editor.pricing_mode === "line_items" ? "bg-[#00E575] text-[#09090B]" : "text-zinc-400 hover:text-zinc-200"}`}>
                      Einzelpositionen
                    </button>
                  </div>
                </div>
                {(() => {
                  const sumNet = linesSumNet;
                  const sumGross = linesSumGross;
                  return (
                    <div className="text-[12px] rounded px-2 py-1.5 mt-1 bg-emerald-500/10 text-emerald-200 border border-emerald-500/30"
                         data-testid="wp-pkg-editor-sum-check">
                      Summe Einzelpreise: <b>{formatEuro(sumNet)} netto</b> · <b>{formatEuro(sumGross)} brutto</b>
                      <span className="ml-2 text-emerald-300/80">= Abgabepreis (live berechnet)</span>
                    </div>
                  );
                })()}
              </div>
              <div>
                <label className={labelCls}>Paket-Name</label>
                <input value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                  data-testid="wp-pkg-editor-name" className={inputCls}
                  placeholder="z. B. HEIKO Thermal Plus 9 kW" />
              </div>
              <div>
                <label className={labelCls}>Beschreibung (im Angebot sichtbar)</label>
                <textarea value={editor.description}
                  onChange={(e) => setEditor({ ...editor, description: e.target.value })}
                  data-testid="wp-pkg-editor-desc"
                  className={`${inputCls} h-20 py-2 resize-none`} />
              </div>

              {/* Bestandteile */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={`${labelCls} mb-0`}>Bestandteile (Info · getauscht/ergänzt im Konfigurator)</label>
                  <div className="flex gap-1.5">
                    <button type="button" onClick={addFreeLine}
                      data-testid="wp-pkg-editor-add-free"
                      className="h-7 px-2.5 text-[10.5px] font-bold uppercase tracking-widest rounded bg-[#0F0F11] border border-dashed border-zinc-500 text-zinc-300 hover:border-[#00E575] hover:text-[#00E575] inline-flex items-center gap-1">
                      <Plus size={11} /> Freie Zeile
                    </button>
                    <button type="button" onClick={pullPricesFromComponents}
                      data-testid="wp-pkg-editor-pull-prices"
                      title="Für alle Katalog-Bestandteile den aktuellen Netto-Preis aus WP-Komponenten als Ref- und Netto-Preis übernehmen."
                      className="h-7 px-2.5 text-[10.5px] font-bold uppercase tracking-widest rounded bg-sky-500/10 border border-sky-500/40 text-sky-300 hover:bg-sky-500/20 inline-flex items-center gap-1">
                      <Zap size={11} /> Preise aus Komponenten
                    </button>
                  </div>
                </div>
                <div className="text-[10.5px] text-zinc-500 mb-1.5 leading-relaxed">
                  <b className="text-zinc-300">Ref-Preis</b> = Preis, den das Item innerhalb dieses Paketes &bdquo;wert&ldquo; ist.
                  Wird beim <b className="text-zinc-300">Tausch</b> oder <b className="text-zinc-300">Entfernen</b> im Angebots-Konfigurator vom Festpreis abgezogen.
                  0 lassen, wenn Item nicht austauschbar sein soll.<br />
                  <b className="text-sky-300">Separate Zeile</b> = Item wird beim Paket-Laden in der Zeilen-Tabelle als eigene bearbeitbare Zeile ausgegeben (z. B. Pufferspeicher). Bundle-Preis reduziert sich um den Ref-Preis.
                </div>
                <div className="rounded-lg border border-zinc-800 bg-[#09090B] p-2 space-y-1">
                  {itemsHydrated.length === 0 && (
                    <div className="text-center text-[11.5px] text-zinc-500 py-4">
                      Noch keine Bestandteile — unten aus dem Katalog wählen oder &bdquo;+ Freie Zeile&ldquo; oben.
                    </div>
                  )}
                  {itemsHydrated.map((it) => (
                    <div key={it._key}
                         className={`flex items-center gap-2 rounded px-2 py-1.5 ${it.is_free ? "bg-[#0F0F11] border border-dashed border-zinc-600" : "bg-[#18181B] border border-zinc-800"}`}>
                      <span className={`text-[9.5px] uppercase tracking-widest rounded px-1.5 py-0.5 shrink-0 ${it.is_free ? "bg-zinc-800/70 text-zinc-300" : "bg-zinc-800 text-zinc-400"}`}>
                        {it.is_free ? "frei" : (it.comp?.category || "?")}
                      </span>
                      <div className="min-w-0 flex-1">
                        {it.is_free ? (
                          <input value={it.title || ""}
                            onChange={(e) => updateFreeTitle(it._key, e.target.value)}
                            data-testid={`wp-pkg-editor-free-title-${it._key}`}
                            placeholder="Bezeichnung (z. B. Sonderleistung Kernbohrung)"
                            className="h-8 w-full bg-transparent border-0 text-[12px] font-semibold text-zinc-100 outline-none focus:bg-[#09090B] focus:border focus:border-[#00E575] rounded px-1" />
                        ) : (
                          <div className="text-[12px] font-semibold truncate">
                            {it.comp?.brand} <span className="text-zinc-400">{it.comp?.model || "—"}</span>
                          </div>
                        )}
                        <input value={it.note || ""} onChange={(e) => updateNote(it._key, e.target.value)}
                          placeholder={it.is_free ? "Beschreibung (optional)" : "Anmerkung (optional)"}
                          className="mt-0.5 h-6 w-full bg-transparent border-0 text-[10.5px] text-zinc-500 focus:text-zinc-300 outline-none px-0" />
                      </div>
                      <div className="flex flex-col items-end shrink-0">
                        <span className="text-[9px] uppercase tracking-widest text-zinc-300">Ref-Preis netto</span>
                        <div className="w-28">
                          <EuroInput
                            value={Number(it.ref_price_net_eur || 0)}
                            onChange={(v) => updateRefPrice(it._key, v)}
                            testid={`wp-pkg-editor-ref-${it._key}`}
                            className="h-8 w-full bg-[#09090B] border border-zinc-700 rounded px-2 text-[11.5px] text-amber-200 outline-none focus:border-[#00E575]"
                          />
                        </div>
                      </div>
                      {editor.pricing_mode === "line_items" && (
                        <div className="flex flex-col items-end shrink-0">
                          <span className="text-[9px] uppercase tracking-widest text-sky-300">Netto-Preis</span>
                          <div className="w-28" title="Netto-Preis dieser Position im Angebot (bei Einzelpositionen-Modus)">
                            <EuroInput
                              value={Number(it.line_price_net_eur || 0)}
                              onChange={(v) => updateLinePrice(it._key, v)}
                              testid={`wp-pkg-editor-lineprice-${it._key}`}
                              className="h-8 w-full bg-[#09090B] border border-sky-500/40 rounded px-2 text-[11.5px] font-mono text-sky-200 outline-none focus:border-[#00E575]"
                            />
                          </div>
                        </div>
                      )}
                      <div className="flex flex-col items-end shrink-0">
                        <span className="text-[9px] uppercase tracking-widest text-zinc-300">Menge</span>
                        <input type="number" min="0" step="0.5" value={it.qty}
                          onChange={(e) => updateQty(it._key, e.target.value)}
                          className="h-8 w-16 bg-[#09090B] border border-zinc-700 rounded px-2 text-right text-[11.5px]" />
                      </div>
                      <label className="flex flex-col items-center shrink-0 cursor-pointer"
                             title="Als eigene Zeile in der Angebots-Tabelle ausgeben (z. B. Speicher)">
                        <span className="text-[9px] uppercase tracking-widest text-zinc-300">Separat</span>
                        <input type="checkbox" checked={!!it.separate_line}
                          onChange={(e) => updateSeparateLine(it._key, e.target.checked)}
                          data-testid={`wp-pkg-editor-separate-${it._key}`}
                          disabled={!it.ref_price_net_eur}
                          className="h-4 w-4 mt-1.5 accent-sky-500 disabled:opacity-40" />
                      </label>
                      <button onClick={() => removeItem(it._key)}
                        data-testid={`wp-pkg-editor-remove-${it._key}`}
                        className="h-7 w-7 rounded bg-red-500/10 border border-red-500/40 text-red-400 hover:bg-red-500/20 inline-flex items-center justify-center shrink-0 self-end">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Picker */}
              <div>
                <label className={labelCls}>Komponente hinzufügen</label>
                <div className="flex gap-1 mb-2 flex-wrap">
                  {Object.entries(CAT_LABELS).map(([k, lbl]) => (
                    <button key={k} onClick={() => setPickerCategory(k)}
                      data-testid={`wp-pkg-picker-cat-${k}`}
                      className={`h-8 px-3 text-[12px] uppercase tracking-widest rounded-lg font-bold ${pickerCategory === k ? "bg-[#00E575] text-[#09090B]" : "bg-[#18181B] border border-zinc-700 text-zinc-300"}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-64 overflow-y-auto">
                  {filteredComponents.map((c) => (
                    <button key={c.id} onClick={() => addItem(c.id)}
                      data-testid={`wp-pkg-picker-add-${c.id}`}
                      className="text-left rounded border border-zinc-800 hover:border-[#00E575]/40 bg-[#09090B] p-2 flex justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-[11.5px] font-semibold truncate">{c.brand} · {c.model}</div>
                        <div className="text-[12px] text-zinc-300 truncate">{c.kw ? `${c.kw} kW · ` : ""}{c.unit || "Stk"}</div>
                      </div>
                      <div className="text-[10.5px] text-[#00E575] font-bold shrink-0">
                        {c.price_eur > 0 ? eur(c.price_eur) : "—"}
                      </div>
                    </button>
                  ))}
                  {filteredComponents.length === 0 && (
                    <div className="col-span-2 text-center text-[11.5px] text-zinc-500 py-4">
                      Keine aktiven Komponenten in dieser Kategorie.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-zinc-800 px-5 py-3 bg-[#0F0F11] flex justify-between items-center">
              <div className="text-[11.5px] text-zinc-400">
                <strong className="text-zinc-100">{editor.items.length}</strong> Bestandteile ·
                Paket-Preis: <strong className="text-[#00E575]">{eur(editor.abgabepreis_brutto)}</strong>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditor(null)}
                  className="h-9 px-3 rounded-lg bg-[#18181B] border border-zinc-700 text-[12px] font-medium">
                  Abbrechen
                </button>
                <button onClick={save} disabled={saving}
                  data-testid="wp-pkg-editor-save"
                  className="h-9 px-4 rounded-lg bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Speichern
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
